<div class="fixed inset-0 z-[8000] bg-black/50 backdrop-blur-sm" (click)="onClose()"></div>

<div
  class="fixed top-0 right-0 z-[8001] h-dvh w-full max-w-[720px] bg-white dark:bg-surface-1 shadow-2xl flex flex-col"
  (click)="$event.stopPropagation()"
  id="prompt_form_panel"
>
  <!-- Header -->
  <div class="flex items-center justify-between px-6 py-5 border-b border-slate-100 dark:border-slate-700/60 shrink-0">
    <div class="flex items-center gap-3">
      <div class="w-8 h-8 rounded-lg bg-gradient-to-br from-purple-500 to-indigo-600 flex items-center justify-center shadow-sm">
        <svg class="w-4 h-4 text-white" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"></path>
        </svg>
      </div>
      <div>
        <h2 class="text-sm font-semibold text-slate-800 dark:text-white">
          {{ mode === 'create' ? 'New Prompt Template' : 'Edit Prompt Template' }}
          <span
            *ngIf="mode === 'edit' && prompt"
            class="ml-1.5 px-1.5 py-0.5 rounded-md bg-slate-100 dark:bg-slate-800 text-[10px] font-bold font-mono text-slate-500 dark:text-slate-400"
            id="prompt_panel_version_badge"
          >v{{ prompt.version }}</span>
        </h2>
        <p class="text-xs text-slate-400 dark:text-slate-500 mt-0.5">
          {{ mode === 'create'
            ? 'A named system prompt the live-call assistant can run with'
            : 'Saving creates a new version — old versions stay in History' }}
        </p>
      </div>
    </div>
    <button
      (click)="onClose()"
      class="flex items-center justify-center w-8 h-8 rounded-lg text-slate-400 hover:text-slate-700 dark:hover:text-white hover:bg-slate-100 dark:hover:bg-slate-700/60 transition-colors"
      id="prompt_panel_close_btn"
    >
      <svg class="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5">
        <line x1="18" y1="6" x2="6" y2="18"></line>
        <line x1="6" y1="6" x2="18" y2="18"></line>
      </svg>
    </button>
  </div>

  <!-- Tabs (edit mode only — a new prompt has no history yet) -->
  <div *ngIf="mode === 'edit'" class="flex gap-1 px-6 pt-3 shrink-0" id="prompt_panel_tabs">
    <button
      type="button"
      (click)="activeTab.set('editor')"
      class="px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors"
      [ngClass]="activeTab() === 'editor'
        ? 'bg-purple-100 dark:bg-purple-900/40 text-purple-700 dark:text-purple-300'
        : 'text-slate-400 hover:text-slate-600 dark:hover:text-slate-300'"
      id="prompt_panel_tab_editor"
    >
      Editor
    </button>
    <button
      type="button"
      (click)="activeTab.set('history')"
      class="px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors"
      [ngClass]="activeTab() === 'history'
        ? 'bg-purple-100 dark:bg-purple-900/40 text-purple-700 dark:text-purple-300'
        : 'text-slate-400 hover:text-slate-600 dark:hover:text-slate-300'"
      id="prompt_panel_tab_history"
    >
      History
    </button>
  </div>

  <!-- Body -->
  <div class="flex-1 overflow-y-auto px-6 py-5 space-y-5">
    <div
      *ngIf="errorMessage()"
      class="p-3 rounded-xl bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800/50 text-xs text-red-700 dark:text-red-400"
      id="prompt_panel_error"
    >
      {{ errorMessage() }}
    </div>

    <!-- ── History tab ─────────────────────────────────────────────────────── -->
    <app-prompt-history-panel
      *ngIf="mode === 'edit' && activeTab() === 'history' && prompt"
      [promptId]="prompt.id"
      (restored)="onRestored()"
    ></app-prompt-history-panel>

    <!-- ── Editor tab ──────────────────────────────────────────────────────── -->
    <ng-container *ngIf="mode === 'create' || activeTab() === 'editor'">
      <!-- name + model -->
      <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label class="block text-xs font-semibold text-slate-600 dark:text-slate-300 mb-1.5">
            Name <span class="text-red-500">*</span>
          </label>
          <input
            type="text"
            [(ngModel)]="form.name"
            placeholder="Default sales prompt"
            class="w-full px-3 py-2.5 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800/50
                   text-sm text-slate-800 dark:text-white placeholder:text-slate-300 dark:placeholder:text-slate-600
                   focus:outline-none focus:ring-2 focus:ring-purple-500/40"
            id="prompt_panel_name"
          />
        </div>

        <div>
          <label class="block text-xs font-semibold text-slate-600 dark:text-slate-300 mb-1.5">
            Voice model
          </label>
          <select
            [(ngModel)]="form.model_id"
            [disabled]="modelsLoading()"
            class="w-full px-3 py-2.5 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800/50
                   text-sm text-slate-800 dark:text-white focus:outline-none focus:ring-2 focus:ring-purple-500/40
                   disabled:opacity-60"
            id="prompt_panel_model"
          >
            <option [ngValue]="''">— No model —</option>
            <optgroup *ngFor="let group of modelGroups()" [label]="providerLabel(group.provider)">
              <option *ngFor="let model of group.models" [ngValue]="model.id">
                {{ model.displayName }} ({{ model.modelCode }}){{ model.isDefault ? ' • in use' : '' }}
              </option>
            </optgroup>
          </select>
          <p class="text-[10px] text-slate-400 mt-1">
            {{ modelsLoading() ? 'Loading models…' : 'The realtime voice model this prompt is written for. See the Models sub-tab.' }}
          </p>
        </div>
      </div>

      <!-- system_prompt -->
      <div>
        <div class="flex items-center justify-between mb-1.5">
          <label class="block text-xs font-semibold text-slate-600 dark:text-slate-300">
            System prompt <span class="text-red-500">*</span>
          </label>
          <span class="text-[10px] text-slate-400 font-mono" id="prompt_panel_text_stats">
            {{ promptLines() | number }} lines · {{ promptChars() | number }} chars
          </span>
        </div>
        <div class="relative">
          <textarea
            [(ngModel)]="form.system_prompt"
            rows="18"
            spellcheck="false"
            [disabled]="loadingPrompt()"
            placeholder="You are a live sales agent for…"
            class="w-full px-3 py-2.5 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800/50
                   text-xs text-slate-800 dark:text-white placeholder:text-slate-300 dark:placeholder:text-slate-600
                   focus:outline-none focus:ring-2 focus:ring-purple-500/40 resize-y font-mono leading-relaxed
                   min-h-[380px] disabled:opacity-60"
            id="prompt_panel_text"
          ></textarea>
          <div
            *ngIf="loadingPrompt()"
            class="absolute inset-0 flex items-center justify-center rounded-xl bg-white/60 dark:bg-slate-900/40"
            id="prompt_panel_text_loading"
          >
            <span class="flex items-center gap-2 text-xs text-slate-500 dark:text-slate-300">
              <span class="w-4 h-4 border-2 border-purple-300 border-t-purple-600 rounded-full animate-spin"></span>
              Loading full prompt…
            </span>
          </div>
        </div>
        <p class="text-[10px] text-slate-400 mt-1">
          The full system instruction the assistant runs with. Long prompts (thousands of lines) are supported.
        </p>
      </div>

      <!-- change notes (edit mode — required, recorded on the new version) -->
      <div *ngIf="mode === 'edit'">
        <label class="block text-xs font-semibold text-slate-600 dark:text-slate-300 mb-1.5">
          What changed <span class="text-red-500">*</span>
        </label>
        <textarea
          [(ngModel)]="form.change_notes"
          rows="2"
          maxlength="512"
          placeholder="e.g. Tightened the greeting, added the refund-policy section"
          class="w-full px-3 py-2.5 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800/50
                 text-xs text-slate-800 dark:text-white placeholder:text-slate-300 dark:placeholder:text-slate-600
                 focus:outline-none focus:ring-2 focus:ring-purple-500/40 resize-y"
          id="prompt_panel_change_notes"
        ></textarea>
        <p class="text-[10px] text-slate-400 mt-1">
          Saved with the new version and shown in History — future you will thank present you.
        </p>
      </div>

      <!-- is_active (create only — the listing row owns the toggle afterwards) -->
      <label
        *ngIf="mode === 'create'"
        class="flex items-start gap-3 p-3 rounded-xl border border-slate-200 dark:border-slate-700 cursor-pointer
               hover:bg-slate-50 dark:hover:bg-slate-800/40 transition-colors"
      >
        <input
          type="checkbox"
          [(ngModel)]="form.is_active"
          class="mt-0.5 w-4 h-4 rounded accent-emerald-600"
          id="prompt_panel_is_active"
        />
        <span>
          <span class="block text-xs font-semibold text-slate-700 dark:text-slate-200">Active</span>
          <span class="block text-[10px] text-slate-400 mt-0.5">
            Inactive prompt templates are kept here but not used by the assistant.
          </span>
        </span>
      </label>
    </ng-container>
  </div>

  <!-- Footer (editor only — History acts inline) -->
  <div
    *ngIf="mode === 'create' || activeTab() === 'editor'"
    class="flex gap-3 px-6 py-4 border-t border-slate-100 dark:border-slate-700/60 shrink-0"
  >
    <button
      (click)="onClose()"
      class="flex-1 py-2.5 rounded-xl border border-slate-200 dark:border-slate-700 text-sm font-medium
             text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700/50 transition-colors"
      id="prompt_panel_cancel_btn"
    >
      Cancel
    </button>
    <button
      (click)="onSubmit()"
      [disabled]="submitting() || loadingPrompt() || (mode === 'edit' && !form.change_notes.trim())"
      class="flex-1 py-2.5 rounded-xl text-sm font-semibold text-white bg-gradient-to-r from-purple-500 to-indigo-600
             hover:from-purple-600 hover:to-indigo-700 disabled:opacity-60 transition-all active:scale-95"
      id="prompt_panel_submit_btn"
    >
      <span *ngIf="!submitting()">{{ mode === 'create' ? 'Create Prompt' : 'Save as New Version' }}</span>
      <span *ngIf="submitting()" class="flex items-center justify-center gap-2">
        <span class="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></span>
        Saving…
      </span>
    </button>
  </div>
</div>
